import { type FC, useMemo } from 'react';
import type { TanStackColumnDef, FilterOption } from '@/components/UI/Table/TableComponent.model';
import APITable from '@/components/UI/Table/Reusable/APITable';
import { RouteService } from '@/support/RouteService';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import { FilterTypes } from '@/constants/FilterTypes';
import UserAvatar from '@/components/UI/UserAvatar/UserAvatar';
import { useSelector } from 'react-redux';
import type { State } from '@/model/State.model';
import type { BaseUser } from '@/model/User.model';
import type { AgentDefinitionV1, AgentSessionSummaryV1 } from '@/model/Agent.model';

interface SessionsTableProps {
  agents: Array<AgentDefinitionV1>;
}

const SessionsTable: FC<SessionsTableProps> = ({ agents }) => {
  const users = useSelector((state: State) => (state.team?.team?.users ?? []) as Array<BaseUser>);

  const filterOptions: Array<FilterOption> = useMemo(() => {
    const userOptions = users.map((user) => ({ name: user.name, value: user.api_id }));
    const agentOptions = agents.map((agent) => ({ name: agent.name, value: agent.api_id }));

    return [
      {
        value: 'user',
        name: 'User',
        operations: [
          { name: 'is equal to', value: FilterTypes.eq, options: userOptions },
          { name: 'is not equal to', value: FilterTypes.ne, options: userOptions },
        ],
      },
      {
        value: 'agent',
        name: 'Agent',
        operations: [
          { name: 'is equal to', value: FilterTypes.eq, options: agentOptions },
          { name: 'is not equal to', value: FilterTypes.ne, options: agentOptions },
        ],
      },
    ];
  }, [users, agents]);

  const columns: Array<TanStackColumnDef<AgentSessionSummaryV1>> = [
    {
      id: 'agent_name',
      header: 'Agent name',
      accessorKey: 'agent_name',
      enableSorting: false,
      isVisible: true,
      size: 180,
    },
    {
      id: 'created_at',
      header: 'Created at',
      accessorKey: 'created_at',
      enableSorting: true,
      isVisible: true,
      size: 100,
      cell: ({ row: { original } }: { row: { original: AgentSessionSummaryV1 } }) => (
        <DateTimeRenderer value={original.created_at} />
      ),
    },
    {
      id: 'created_by',
      header: 'Created by',
      accessorKey: 'created_by',
      enableSorting: false,
      isVisible: true,
      size: 20,
      cell: ({ row: { original } }: { row: { original: AgentSessionSummaryV1 } }) => (
        <UserAvatar
          user={original.created_by ?? undefined}
          className="ml1 dib"
          tooltip={original.created_by?.name ?? undefined}
        />
      ),
    },
  ];

  return (
    <APITable
      apiInfo={{
        type: 'internalApi',
        route: RouteService.api({
          endpoint: 'GET /api/v1/ai-agents/sessions',
          path: undefined,
        }).url,
      }}
      columns={columns}
      filterOptions={filterOptions}
      defaultSortBy={{ id: 'created_at', desc: true }}
      reduxTableName="agentSessions"
      showViewSelector
      hideSearch
      noDataMessage="Sessions from your agent runs will appear here."
      testIdPrefix="agent-sessions"
    />
  );
};

export default SessionsTable;
